import React from 'react';
import { calculateDuration, convertTimeToMinutes, concatenateDateTime } from 'utilities/calendarFunctions';
import { ExpandButton, TimeSlotButton } from "../../comps/calendar";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis, faExpand } from '@fortawesome/free-solid-svg-icons';

// Types for the record and props
interface DateTimeInfo {
	date: string;
	startTime: string;
	endTime: string;
}

interface RecordType {
	id?: string | number;
	label: string;
	dateTimeInfo: DateTimeInfo;
	[key: string]: any; // Allow extra fields
}

interface GroupedRecordsProps {
	dataType: string;
	records: RecordType[];
	hourHeight: number;
	startingHour: string;
	showDayView: (date: string) => void;
	onRecordClick: (record: RecordType) => void;
}

interface GroupedRecordGroup {
	records: RecordType[];
	duration: number;
	groupHeight: number;
	offsetTop: number;
	start: Date;
}

const GroupedRecords: React.FC<GroupedRecordsProps> = ({
	dataType,
	records,
	hourHeight,
	startingHour,
	showDayView,
	onRecordClick,
}) => {
	// Sort records by start datetime
	const sortedRecords = [...records].sort(
		(a, b) =>
			new Date(concatenateDateTime(a.dateTimeInfo.date, a.dateTimeInfo.startTime)).getTime() -
			new Date(concatenateDateTime(b.dateTimeInfo.date, b.dateTimeInfo.startTime)).getTime()
	);

	const groupedRecords: GroupedRecordGroup[] = [];

	let currentGroup: RecordType[] = [];
	let currentGroupStart: string | null = null;
	let currentGroupEnd: string | null = null;

	const calculateOffsetStart = () => {
		const startTime = new Date('2024-01-01T' + startingHour);
		const hours = startTime.getHours();
		const minutes = startTime.getMinutes();
		const totalMinutes = hours * 60 + minutes;
		const calculatedOffset = (totalMinutes / 60) * hourHeight;
		return calculatedOffset;
	};

	const startDayOffset = startingHour !== '' ? calculateOffsetStart() : 0;

	sortedRecords.forEach(record => {
		const startDateTime = concatenateDateTime(record.dateTimeInfo.date, record.dateTimeInfo.startTime);
		const endDateTime = concatenateDateTime(record.dateTimeInfo.date, record.dateTimeInfo.endTime);

		if (
			currentGroup.length === 0 ||
			(currentGroupEnd && new Date(currentGroupEnd) >= new Date(startDateTime))
		) {
			currentGroup.push(record);

			if (currentGroupEnd === null || new Date(endDateTime) > new Date(currentGroupEnd)) {
				currentGroupEnd = endDateTime;
			}

			if (currentGroupStart === null || new Date(startDateTime) < new Date(currentGroupStart)) {
				currentGroupStart = startDateTime;
			}
		} else {
			const start = new Date(currentGroupStart!);
			const duration = calculateDuration(currentGroupStart!, currentGroupEnd!);
			const groupHeight = (duration / 60) * hourHeight - 1;
			const offsetTop = (convertTimeToMinutes(currentGroupStart!) / 60) * hourHeight - startDayOffset;
			groupedRecords.push({ records: currentGroup, duration, groupHeight, offsetTop, start });

			currentGroup = [record];
			currentGroupStart = startDateTime;
			currentGroupEnd = endDateTime;
		}
	});

	if (currentGroup.length > 0 && currentGroupStart && currentGroupEnd) {
		const start = new Date(currentGroupStart);
		const duration = calculateDuration(currentGroupStart, currentGroupEnd);
		const groupHeight = (duration / 60) * hourHeight - 1;
		const offsetTop = (convertTimeToMinutes(currentGroupStart) / 60) * hourHeight - startDayOffset;
		groupedRecords.push({ records: currentGroup, duration, groupHeight, offsetTop, start });
	}

	return (
		<>
			<div className={`${dataType}-col`}>
				{groupedRecords.map((group, index) => (
					<div
						key={index}
						className={`times-group ${dataType}-group`}
						style={{ top: group.offsetTop, height: group.groupHeight }}
					>
						<div className='timeslot-buttons'>
							{group.records.map((record, innerIndex) => (
								<TimeSlotButton
									key={innerIndex}
									dataType={dataType}
									startTime={concatenateDateTime(record.dateTimeInfo.date, record.dateTimeInfo.startTime)}
									endTime={concatenateDateTime(record.dateTimeInfo.date, record.dateTimeInfo.endTime)}
									label={record.label}
									onClick={() => onRecordClick(record)}
								/>
							))}
						</div>
						<ExpandButton
							groupingType={dataType}
							icon={
								<FontAwesomeIcon
									icon={group.records.length * 36 > group.groupHeight ? faEllipsis : faExpand}
								/>
							}
							onClick={() => showDayView(group.start.toLocaleDateString('en-CA'))}
						/>
					</div>
				))}
			</div>
		</>
	);
};

export default GroupedRecords;